import { ArrowLeft, Calendar, Clock, User, CheckCircle, Gift, Star, Music, Smile, Zap, Monitor, Shuffle } from 'lucide-react';
import { useTranslation } from '../utils/useTranslation';

interface BlogPost4Props {
    onBack: () => void;
}

export function BlogPost4({ onBack }: BlogPost4Props) {
    const { t, tObject } = useTranslation();

    const meta = tObject('blogPost4.meta') as any;
    const intro = tObject('blogPost4.intro') as any;
    const section1 = tObject('blogPost4.section1') as any;
    const section2 = tObject('blogPost4.section2') as any;
    const section3 = tObject('blogPost4.section3') as any;
    const section4 = tObject('blogPost4.section4') as any;
    const section5 = tObject('blogPost4.section5') as any;
    const cta = tObject('blogPost4.cta') as any;

    return (
        <div className="min-h-screen bg-white pt-24 pb-12">
            {/* Hero Section */}
            <div className="relative h-[500px] w-full overflow-hidden mb-12">
                <div className="absolute inset-0 bg-gradient-to-br from-purple-600 to-pink-600 opacity-90"></div>
                <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1511578314322-379afb476865?auto=format&fit=crop&q=80')] bg-cover bg-center mix-blend-overlay"></div>
                <div className="absolute inset-0 flex flex-col items-center justify-center text-white p-4 text-center">
                    <span className="px-4 py-1 bg-white/20 backdrop-blur-md rounded-full text-sm font-medium mb-6 border border-white/30">
                        {meta?.badge}
                    </span>
                    <h1 className="text-4xl md:text-6xl font-bold max-w-4xl mb-6 leading-tight drop-shadow-lg">
                        {meta?.title}
                    </h1>
                    <div className="flex flex-wrap items-center justify-center gap-6 text-sm md:text-base opacity-90">
                        <div className="flex items-center gap-2">
                            <User className="w-5 h-5" />
                            <span>{meta?.author}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Calendar className="w-5 h-5" />
                            <span>{meta?.date}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Clock className="w-5 h-5" />
                            <span>{meta?.readTime}</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="container mx-auto px-4 max-w-4xl">
                <button
                    onClick={onBack}
                    className="group flex items-center gap-2 text-gray-600 hover:text-purple-600 transition-colors mb-8 font-medium"
                >
                    <div className="p-2 bg-gray-100 rounded-full group-hover:bg-purple-50 transition-colors">
                        <ArrowLeft className="w-5 h-5" />
                    </div>
                    {meta?.backButton}
                </button>

                {/* Content Container */}
                <article className="prose prose-lg max-w-none">
                    <p className="lead text-2xl text-gray-600 mb-10 font-light leading-relaxed border-l-4 border-purple-500 pl-6 italic">
                        {intro?.lead}
                    </p>

                    <div className="bg-purple-50 rounded-2xl p-8 mb-12 border border-purple-100">
                        <h3 className="text-purple-800 font-bold text-xl mb-4 flex items-center gap-2">
                            <Star className="w-5 h-5 fill-current" />
                            {intro?.menuTitle}
                        </h3>
                        <div className="grid md:grid-cols-2 gap-4">
                            <ul className="list-none space-y-3 m-0">
                                <li className="flex items-center gap-2 text-gray-700">
                                    <Zap className="w-4 h-4 text-yellow-500" />
                                    <strong>{intro?.menu?.icebreakers?.split(':')[0]}:</strong> {intro?.menu?.icebreakers?.split(':').slice(1).join(':')}
                                </li>
                                <li className="flex items-center gap-2 text-gray-700">
                                    <CheckCircle className="w-4 h-4 text-blue-500" />
                                    <strong>{intro?.menu?.corporate?.split(':')[0]}:</strong> {intro?.menu?.corporate?.split(':').slice(1).join(':')}
                                </li>
                                <li className="flex items-center gap-2 text-gray-700">
                                    <User className="w-4 h-4 text-red-500" />
                                    <strong>{intro?.menu?.family?.split(':')[0]}:</strong> {intro?.menu?.family?.split(':').slice(1).join(':')}
                                </li>
                            </ul>
                            <ul className="list-none space-y-3 m-0">
                                <li className="flex items-center gap-2 text-gray-700">
                                    <Monitor className="w-4 h-4 text-green-500" />
                                    <strong>{intro?.menu?.remote?.split(':')[0]}:</strong> {intro?.menu?.remote?.split(':').slice(1).join(':')}
                                </li>
                                <li className="flex items-center gap-2 text-gray-700">
                                    <Shuffle className="w-4 h-4 text-pink-500" />
                                    <strong>{intro?.menu?.variations?.split(':')[0]}:</strong> {intro?.menu?.variations?.split(':').slice(1).join(':')}
                                </li>
                            </ul>
                        </div>
                    </div>

                    {/* Seção 1: Quebra-Gelo */}
                    <section className="mb-16">
                        <h2 className="text-4xl font-bold text-gray-800 mt-12 mb-6 flex items-center gap-3 border-b-2 border-yellow-200 pb-4">
                            <span className="flex items-center justify-center w-12 h-12 bg-yellow-500 text-white rounded-xl text-2xl font-bold">1</span>
                            {section1?.title}
                        </h2>
                        <p className="text-gray-700 mb-6">{section1?.intro}</p>

                        <h3 className="text-2xl font-bold text-gray-800 mt-8 mb-4">{section1?.bingo?.title}</h3>
                        <p className="text-gray-700 mb-4">
                            <strong>{section1?.bingo?.ideal?.split(':')[0]}:</strong> {section1?.bingo?.ideal?.split(':').slice(1).join(':')}
                        </p>
                        <p className="text-gray-700 mb-4">{section1?.bingo?.description}</p>
                        <div className="bg-yellow-50 p-6 rounded-xl mb-6">
                            <h4 className="font-bold text-yellow-800 mb-2">{section1?.bingo?.howToPlayTitle}</h4>
                            <ol className="list-decimal pl-5 text-gray-700 space-y-3">
                                {section1?.bingo?.steps?.map((step: string, i: number) => (
                                    <li key={i}>{step}</li>
                                ))}
                            </ol>
                        </div>

                        <h3 className="text-2xl font-bold text-gray-800 mt-8 mb-4">{section1?.twoTruths?.title}</h3>
                        <p className="text-gray-700 mb-4">
                            <strong>{section1?.twoTruths?.ideal?.split(':')[0]}:</strong> {section1?.twoTruths?.ideal?.split(':').slice(1).join(':')}
                        </p>
                        <p className="text-gray-700 mb-4">{section1?.twoTruths?.description}</p>
                        <p className="text-gray-700 mb-4">
                            <strong>{section1?.twoTruths?.proTip?.split(':')[0]}:</strong> {section1?.twoTruths?.proTip?.split(':').slice(1).join(':')}
                        </p>
                    </section>

                    {/* Seção 2: Corporativo */}
                    <section className="mb-16">
                        <h2 className="text-4xl font-bold text-gray-800 mt-12 mb-6 flex items-center gap-3 border-b-2 border-blue-200 pb-4">
                            <span className="flex items-center justify-center w-12 h-12 bg-blue-600 text-white rounded-xl text-2xl font-bold">2</span>
                            {section2?.title}
                        </h2>
                        <p className="text-gray-700 mb-6">{section2?.intro}</p>

                        <h3 className="text-2xl font-bold text-gray-800 mt-8 mb-4">{section2?.marshmallow?.title}</h3>
                        <p className="text-gray-700 mb-4">
                            <strong>{section2?.marshmallow?.material?.split(':')[0]}:</strong> {section2?.marshmallow?.material?.split(':').slice(1).join(':')}
                        </p>
                        <div className="bg-blue-50 p-6 rounded-xl mb-6">
                            <h4 className="font-bold text-blue-800 mb-2">{section2?.marshmallow?.missionTitle}</h4>
                            <p className="text-gray-700 mb-3">{section2?.marshmallow?.mission}</p>
                            <p className="text-gray-700 mt-2">
                                <strong>{section2?.marshmallow?.why?.split('?')[0]}?</strong> {section2?.marshmallow?.why?.split('?').slice(1).join('?')}
                            </p>
                        </div>

                        <h3 className="text-2xl font-bold text-gray-800 mt-8 mb-4">{section2?.whoAmI?.title}</h3>
                        <p className="text-gray-700 mb-4">{section2?.whoAmI?.description1}</p>
                        <p className="text-gray-700 mb-4">{section2?.whoAmI?.description2}</p>
                        <p className="text-gray-700">
                            <strong>{section2?.whoAmI?.variation?.split(':')[0]}:</strong> {section2?.whoAmI?.variation?.split(':').slice(1).join(':')}
                        </p>
                    </section>

                    {/* Seção 3: Família e Amigos */}
                    <section className="mb-16">
                        <h2 className="text-4xl font-bold text-gray-800 mt-12 mb-6 flex items-center gap-3 border-b-2 border-red-200 pb-4">
                            <span className="flex items-center justify-center w-12 h-12 bg-red-600 text-white rounded-xl text-2xl font-bold">3</span>
                            {section3?.title}
                        </h2>
                        <p className="text-gray-700 mb-6">{section3?.intro}</p>

                        <h3 className="text-2xl font-bold text-gray-800 mt-8 mb-4">{section3?.charades?.title}</h3>
                        <p className="text-gray-700 mb-4">{section3?.charades?.description1}</p>
                        <p className="text-gray-700 mb-4">
                            <strong>{section3?.charades?.variation?.split(':')[0]}:</strong> {section3?.charades?.variation?.split(':').slice(1).join(':')}
                        </p>

                        <h3 className="text-2xl font-bold text-gray-800 mt-8 mb-4">{section3?.music?.title}</h3>
                        <p className="text-gray-700 mb-4">{section3?.music?.description}</p>
                    </section>

                    {/* Seção 4: Digital / Remoto */}
                    <section className="mb-16">
                        <h2 className="text-4xl font-bold text-gray-800 mt-12 mb-6 flex items-center gap-3 border-b-2 border-green-200 pb-4">
                            <span className="flex items-center justify-center w-12 h-12 bg-green-600 text-white rounded-xl text-2xl font-bold">4</span>
                            {section4?.title}
                        </h2>
                        <p className="text-gray-700 mb-6">{section4?.intro}</p>

                        <h3 className="text-2xl font-bold text-gray-800 mt-8 mb-4">{section4?.gartic?.title}</h3>
                        <p className="text-gray-700 mb-4">{section4?.gartic?.description1}</p>
                        <p className="text-gray-700 mb-4">{section4?.gartic?.description2}</p>

                        <h3 className="text-2xl font-bold text-gray-800 mt-8 mb-4">{section4?.kahoot?.title}</h3>
                        <p className="text-gray-700 mb-4">{section4?.kahoot?.description1}</p>
                        <p className="text-gray-700 mb-4">{section4?.kahoot?.description2}</p>
                    </section>

                    {/* Seção 5: Variações de Amigo Secreto */}
                    <section className="mb-16">
                        <h2 className="text-4xl font-bold text-gray-800 mt-12 mb-6 flex items-center gap-3 border-b-2 border-pink-200 pb-4">
                            <span className="flex items-center justify-center w-12 h-12 bg-pink-500 text-white rounded-xl text-2xl font-bold">5</span>
                            {section5?.title}
                        </h2>
                        <p className="text-gray-700 mb-6">{section5?.intro}</p>

                        <h3 className="text-2xl font-bold text-gray-800 mt-8 mb-4">{section5?.whiteElephant?.title}</h3>
                        <p className="text-gray-700 mb-4">{section5?.whiteElephant?.intro}</p>
                        <div className="bg-pink-50 p-6 rounded-xl mb-6">
                            <h4 className="font-bold text-pink-800 mb-2">{section5?.whiteElephant?.rulesTitle}</h4>
                            <ol className="list-decimal pl-5 text-gray-700 space-y-3">
                                {section5?.whiteElephant?.rules?.map((rule: string, i: number) => (
                                    <li key={i}><strong>{rule.split(':')[0]}:</strong> {rule.split(':').slice(1).join(':')}</li>
                                ))}
                            </ol>
                            <p className="text-gray-700 mt-2">
                                <strong>{section5?.whiteElephant?.result?.split(':')[0]}:</strong> {section5?.whiteElephant?.result?.split(':').slice(1).join(':')}
                            </p>
                        </div>
                    </section>

                    <hr className="my-12 border-gray-200" />

                    {/* Conclusão e CTA */}
                    <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-3xl p-8 md:p-16 text-white text-center shadow-2xl relative overflow-hidden transform transition-all hover:scale-[1.01]">
                        <div className="absolute top-0 left-0 w-full h-full bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
                        <Music className="w-24 h-24 mx-auto mb-8 text-pink-200 animate-bounce drop-shadow-md" />
                        <h2 className="text-3xl md:text-5xl font-bold mb-8 relative z-10 leading-tight">{cta?.title}</h2>
                        <p className="text-xl opacity-90 mb-12 max-w-3xl mx-auto leading-relaxed relative z-10 font-light">
                            {cta?.description}
                        </p>
                        <button
                            onClick={onBack}
                            className="bg-white text-purple-600 px-12 py-6 rounded-full font-bold text-xl hover:bg-purple-50 hover:shadow-2xl hover:-translate-y-1 transition-all shadow-xl relative z-10 flex items-center gap-4 mx-auto group"
                        >
                            <Gift className="w-7 h-7 group-hover:rotate-12 transition-transform" />
                            {cta?.button}
                        </button>
                    </div>
                </article>
            </div>
        </div>
    );
}